import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceArea } from 'recharts';
import styled from 'styled-components';
import { getLabDataByName, updateLabData } from '../services/labService';
import { getProfile, parseProfileForFormula } from '../services/profileService';
import MainHeader from '../Components/MainHeader';
import CustomAlert from '../Components/CustomAlert';
import { useCustomAlert } from '../hooks/useCustomAlert';
import CreatinineFormula from '../formula/Creatinine';
import EgfrFormula from '../formula/Egfr';
import BunFormula from '../formula/Bun';

const KidneyContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: normal;
  margin-top: 4px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #666;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin: 0 0 16px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#FF5252'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const KidneyIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusValue = styled.div`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 2px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusLabel = styled.div`
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AbnormalName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 4px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const AbnormalStatus = styled.div`
  font-size: 14px;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const ChartSection = styled.div`
  margin-bottom: 40px;
`;

const ChartTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Legend = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const HistorySection = styled.div`
  margin-top: 40px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #00BBD3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: #34A853;
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00BBD3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const KidneyPage = () => {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useCustomAlert();
  const [loading, setLoading] = useState(true);
  const [creatinineData, setCreatinineData] = useState([]);
  const [bunData, setBunData] = useState([]);
  const [egfrData, setEgfrData] = useState([]);
  const [latestDate, setLatestDate] = useState('');
  const [latestValues, setLatestValues] = useState({
    creatinine: { value: '-', status: 'ปกติ', color: '#34A853' },
    egfr: { value: '-', status: 'ปกติ', color: '#34A853' },
    bun: { value: '-', status: 'ปกติ', color: '#34A853' }
  });
  const [profileData, setProfileData] = useState({ age: 0, gender: '' });
  const [allRecords, setAllRecords] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    bloodPressure: '',
    risk: ''
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      creatinine: record.creatinine || '',
      bun: record.bun || '',
      egfr: record.egfr || ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {

      // Update each value if it has changed
      const updates = [];

      if (editForm.creatinine && editForm.creatinine !== editingRecord.creatinine) {
        updates.push(
          updateLabData(editingRecord.rawDate, 'creatinine', editForm.creatinine, 'h')
        );
      }

      if (editForm.bun && editForm.bun !== editingRecord.bun) {
        updates.push(
          updateLabData(editingRecord.rawDate, 'bun', editForm.bun, 'h')
        );
      }

      if (editForm.egfr && editForm.egfr !== editingRecord.egfr) {
        updates.push(
          updateLabData(editingRecord.rawDate, 'egfr', editForm.egfr, 'h')
        );
      }

      await Promise.all(updates);

      showAlert('อัปเดตข้อมูลเรียบร้อยแล้ว');

      // Reload data
      await loadKidneyData();

      // Close modal
      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      showAlert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = (date.getFullYear() + 543).toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const calculateRisk = (creatinine, bun, egfr) => {
    const creatinineVal = parseFloat(creatinine) || 0;
    const bunVal = parseFloat(bun) || 0;
    const egfrVal = parseFloat(egfr) || 0;

    if (!creatinine && !bun && !egfr) return '-';

    const isCreatinineAbnormal = creatinineVal > 0 && (creatinineVal < 0.6 || creatinineVal > 1.2);
    const isBunAbnormal = bunVal > 0 && (bunVal < 7 || bunVal > 20);
    const isEgfrAbnormal = egfrVal > 0 && egfrVal < 60;

    if (isCreatinineAbnormal || isBunAbnormal || isEgfrAbnormal) {
      return 'ผิดปกติ';
    }

    return 'ปกติ';
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return '';

    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const thaiYear = date.getFullYear() + 543;
    const thaiMonth = thaiMonths[date.getMonth()];

    return `${date.getDate()} ${thaiMonth} ${thaiYear}`;
  };

  const loadKidneyData = async () => {
    try {
      setLoading(true);

      const profileResponse = await getProfile();
      const profile = profileResponse?.data || profileResponse?.profile || {};
      const { age, gender } = parseProfileForFormula(profile);

      setProfileData({ age, gender });

      // ดึงข้อมูลจาก GetLabDataByName API และ sort descending (ใหม่→เก่า)
      const creatinineResponse = await getLabDataByName('creatinine');
      let creatinineArray = (creatinineResponse?.data || []).sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA;
      });

      const bunResponse = await getLabDataByName('bun');
      let bunArray = (bunResponse?.data || []).sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA;
      });

      const egfrResponse = await getLabDataByName('egfr');
      let egfrArray = (egfrResponse?.data || []).sort((a, b) => {
        const dateA = new Date(a.dateadd).getTime();
        const dateB = new Date(b.dateadd).getTime();
        return dateB - dateA;
      });


      // Combine all records by date for table display
      const recordsMap = new Map();

      creatinineArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, creatinine: null, bun: null, egfr: null });
        }
        recordsMap.get(date).creatinine = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      bunArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, creatinine: null, bun: null, egfr: null });
        }
        recordsMap.get(date).bun = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      egfrArray.forEach(item => {
        const date = item.dateadd?.split('T')[0].split(' ')[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, { date, rawDate: item.dateadd, creatinine: null, bun: null, egfr: null });
        }
        recordsMap.get(date).egfr = item.value;
        if (!recordsMap.get(date).rawDate) recordsMap.get(date).rawDate = item.dateadd;
      });

      // Convert map to array and sort by date (newest first)
      const combinedRecords = Array.from(recordsMap.values()).sort((a, b) =>
        new Date(b.date) - new Date(a.date)
      );

      setAllRecords(combinedRecords);


      // Process Creatinine data for chart
      const processedCreatinine = creatinineArray.map(item => ({
        date: item.dateadd?.split('T')[0].split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      // Process BUN data for chart
      const processedBun = bunArray.map(item => ({
        date: item.dateadd?.split('T')[0].split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      // Process eGFR data for chart
      const processedEgfr = egfrArray.map(item => ({
        date: item.dateadd?.split('T')[0].split(' ')[0] || '',
        value: parseFloat(item.value) || 0,
        rawDate: item.dateadd
      })).reverse();

      setCreatinineData(processedCreatinine);
      setBunData(processedBun);
      setEgfrData(processedEgfr);

      // Extract latest date from all data sources
      // ใช้ [0] เพื่อหาวันที่ล่าสุด (array sort descending แล้ว)
      let mostRecentDate = '';
      if (creatinineArray.length > 0) {
        mostRecentDate = creatinineArray[0].dateadd;
      }
      if (bunArray.length > 0 && bunArray[0].dateadd > mostRecentDate) {
        mostRecentDate = bunArray[0].dateadd;
      }
      if (egfrArray.length > 0 && egfrArray[0].dateadd > mostRecentDate) {
        mostRecentDate = egfrArray[0].dateadd;
      }

      if (mostRecentDate) {
        setLatestDate(formatThaiDate(mostRecentDate));
      }

      // Set latest values using formula
      if (creatinineArray.length > 0 || egfrArray.length > 0 || bunArray.length > 0) {
        // ใช้ [0] เพราะ array ถูก sort descending แล้ว (ใหม่→เก่า)
        const latestCreatinine = creatinineArray[0];
        const latestEgfr = egfrArray[0];
        const latestBun = bunArray[0];

        // Calculate Creatinine status using formula
        const creatinineValue = parseFloat(latestCreatinine?.value || 0);
        const creatinineResult = latestCreatinine ? await CreatinineFormula(creatinineValue, age, gender) : null;
        const creatinineColorMap = { 'green': '#34A853', 'red': '#ff4757', 'gray': '#e0e0e0' };

        // Calculate eGFR status using formula
        const egfrValue = parseFloat(latestEgfr?.value || 0);
        const egfrResult = latestEgfr ? EgfrFormula(egfrValue) : null;

        // Calculate BUN status using formula
        const bunValue = parseFloat(latestBun?.value || 0);
        const bunResult = latestBun ? await BunFormula(bunValue, age, gender) : null;

        setLatestValues({
          creatinine: {
            value: latestCreatinine ? `${creatinineValue.toFixed(2)} mg/dL` : '-',
            status: creatinineResult?.title || 'ปกติ',
            color: creatinineResult ? (creatinineColorMap[creatinineResult.color] || '#34A853') : '#34A853',
            rawValue: creatinineValue
          },
          egfr: {
            value: latestEgfr ? `${egfrValue.toFixed(1)} mL/min` : '-',
            status: egfrResult?.title || 'ปกติ',
            color: egfrResult ? (creatinineColorMap[egfrResult.color] || '#34A853') : '#34A853',
            rawValue: egfrValue
          },
          bun: {
            value: latestBun ? `${bunValue.toFixed(1)} mg/dL` : '-',
            status: bunResult?.title || 'ปกติ',
            color: bunResult ? (creatinineColorMap[bunResult.color] || '#34A853') : '#34A853',
            rawValue: bunValue
          }
        });
      }

    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKidneyData();
  }, []);

  return (
    <KidneyContainer>
      <MainHeader />

      <TopBar>ไต</TopBar>

      <Header>
        <BackButton onClick={handleBack}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle>ไต</PageTitle>
        {latestDate && <DateText>บันทึกล่าสุดวันที่ {latestDate}</DateText>}
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : (
          <>
            {/* Status Card */}
            {(() => {
              // Check which values are abnormal (not green)
              const isCreatinineAbnormal = latestValues.creatinine.value !== '-' && latestValues.creatinine.color !== '#34A853';
              const isEgfrAbnormal = latestValues.egfr.value !== '-' && latestValues.egfr.color !== '#34A853';
              const isBunAbnormal = latestValues.bun?.value !== '-' && latestValues.bun.color !== '#34A853';

              const hasAbnormal = isCreatinineAbnormal || isEgfrAbnormal || isBunAbnormal;

              // Determine overall card color (use worst color)
              let cardColor = '#34A853'; // default green
              if (hasAbnormal) {
                // Find the worst color among abnormal values
                const colors = [];
                if (isCreatinineAbnormal) colors.push(latestValues.creatinine.color);
                if (isEgfrAbnormal) colors.push(latestValues.egfr.color);
                if (isBunAbnormal) colors.push(latestValues.bun.color);

                // Priority: red > orange > yellow > green
                if (colors.includes('#ff4757')) cardColor = '#ff4757';
                else if (colors.includes('#FF9800')) cardColor = '#FF9800';
                else if (colors.includes('#FBBC04')) cardColor = '#FBBC04';
              }

              return (
                <StatusCard $bgColor={cardColor}>
                  <KidneyIcon src="./ไตสี.png" alt="ไต" />
                  <StatusInfo>
                    {!hasAbnormal ? (
                      <StatusTitle style={{ color: 'white' }}>ผลตรวจปกติ</StatusTitle>
                    ) : (
                      <>
                        {isCreatinineAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>Creatinine</AbnormalName>
                            <AbnormalValue>{latestValues.creatinine.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.creatinine.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isEgfrAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>eGFR</AbnormalName>
                            <AbnormalValue>{latestValues.egfr.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.egfr.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isBunAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>BUN</AbnormalName>
                            <AbnormalValue>{latestValues.bun.value}</AbnormalValue>
                            <AbnormalStatus>{latestValues.bun.status}</AbnormalStatus>
                          </AbnormalItem>
                        )}
                      </>
                    )}
                  </StatusInfo>
                </StatusCard>
              );
            })()}

            {/* Creatinine Chart */}
            <ChartSection>
              <ChartTitle>Creatinine</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFCDD2" />
                  <span>ต่ำ</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#C8E6C9" />
                  <span>ปกติ</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFCDD2" />
                  <span>สูง</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={creatinineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 2]} />

                  {/* ต่ำ (< 0.6) */}
                  <ReferenceArea y1={0} y2={0.6} fill="#FFEBEE" fillOpacity={0.6} />
                  {/* ปกติ (0.6-1.2) */}
                  <ReferenceArea y1={0.6} y2={1.2} fill="#E8F5E9" fillOpacity={0.6} />
                  {/* สูง (> 1.2) */}
                  <ReferenceArea y1={1.2} y2={2} fill="#FFEBEE" fillOpacity={0.6} />

                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2196F3"
                    strokeWidth={2}
                    dot={{ fill: '#2196F3', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* BUN Chart */}
            <ChartSection>
              <ChartTitle>BUN , Urea (Blood Urea Nitrogen)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFCDD2" />
                  <span>ต่ำ</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#C8E6C9" />
                  <span>ปกติ</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFCDD2" />
                  <span>สูง</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={bunData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 30]} />

                  {/* ต่ำ (< 7) */}
                  <ReferenceArea y1={0} y2={7} fill="#FFEBEE" fillOpacity={0.6} />
                  {/* ปกติ (7-20) */}
                  <ReferenceArea y1={7} y2={20} fill="#E8F5E9" fillOpacity={0.6} />
                  {/* สูง (> 20) */}
                  <ReferenceArea y1={20} y2={30} fill="#FFEBEE" fillOpacity={0.6} />

                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2196F3"
                    strokeWidth={2}
                    dot={{ fill: '#2196F3', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* eGFR Chart */}
            <ChartSection>
              <ChartTitle>eGFR</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFCDD2" />
                  <span>ต่ำ</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#C8E6C9" />
                  <span>ปกติ</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={egfrData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 120]} />

                  {/* ต่ำ (< 60) - ผิดปกติ */}
                  <ReferenceArea y1={0} y2={60} fill="#FFEBEE" fillOpacity={0.6} />
                  {/* ปกติ (≥ 60) */}
                  <ReferenceArea y1={60} y2={120} fill="#E8F5E9" fillOpacity={0.6} />

                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2196F3"
                    strokeWidth={2}
                    dot={{ fill: '#2196F3', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* History Table */}
            <HistorySection>
              <HistoryTitle>ประวัติการตรวจ</HistoryTitle>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell>ความเสี่ยง</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {allRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                        ไม่พบข้อมูล
                      </TableCell>
                    </TableRow>
                  ) : (
                    allRecords.map((record, index) => {
                      const values = [];
                      if (record.creatinine) values.push(`Cr: ${record.creatinine}`);
                      if (record.bun) values.push(`BUN: ${record.bun}`);
                      if (record.egfr) values.push(`eGFR: ${record.egfr}`);

                      return (
                        <TableRow key={index}>
                          <TableCell>{formatDisplayDate(record.date)}</TableCell>
                          <TableCell>{values.join(', ') || '-'}</TableCell>
                          <TableCell>{calculateRisk(record.creatinine, record.bun, record.egfr)}</TableCell>
                          <TableCell>
                            <EditButton onClick={() => handleEditClick(record)}>
                              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </EditButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {/* Edit Modal */}
      {showEditModal && editingRecord && (
        <Modal onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>

            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            <FormGroup>
              <Label>Creatinine (mg/dL)</Label>
              <Input
                type="number"
                step="0.01"
                value={editForm.creatinine}
                onChange={(e) => setEditForm({ ...editForm, creatinine: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>BUN (mg/dL)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.bun}
                onChange={(e) => setEditForm({ ...editForm, bun: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>eGFR (mL/min)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.egfr}
                onChange={(e) => setEditForm({ ...editForm, egfr: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleModalClose}>ยกเลิก</CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {alertState.isOpen && (
        <CustomAlert message={alertState.message} onClose={closeAlert} />
      )}
    </KidneyContainer>
  );
};

export default KidneyPage;
